<template>
  <div class="table-wrapper">
    <div class="distribution-main d-md-flex">
      <b-button
        v-if="$can('create', 'distributions', 'cdn')"
        variant="bg-primary"
        to="create-distribution"
      >
        <GjIcon
          class="icon-left"
          name="Plus"
        />
        Create Distribution
      </b-button>
      <b-button
        v-if="$can('get', 'distributions', 'cdn')"
        variant="bg-white"
        :disabled="!isSelected"
        class="d-none d-lg-flex"
        @click="idClick(selected[0].distributionId)"
      >
        Distribution Settings
      </b-button>
      <button-loading
        v-if="$can('delete', 'distributions', 'cdn')"
        variant="bg-white"
        :disabled="!isSelected"
        :spinning="removingDistribution"
        class="d-none d-lg-flex"
        @click="onDeleteDistributionDialog(selected[0].distributionId)"
      >
        Delete
      </button-loading>
      <button-loading
        v-if="$can('update', 'distributions', 'cdn')"
        variant="bg-white"
        :disabled="!canEnable"
        :spinning="enableDistribution"
        class="d-none d-lg-flex"
        @click="toggleState(1, selected[0].distributionId)"
      >
        Enable
      </button-loading>
      <button-loading
        v-if="$can('update', 'distributions', 'cdn')"
        variant="bg-white"
        :disabled="!canDisable"
        :spinning="disableDistribution"
        class="d-none d-lg-flex"
        @click="toggleState(0, selected[0].distributionId)"
      >
        Disable
      </button-loading>
      <button-loading
        v-if="$can('update', 'distributions', 'cdn')"
        variant="bg-white"
        :disabled="!isSelected"
        :spinning="updatingDistributionState"
        class="d-none d-lg-flex"
        @click="
          toggleState(
            selected[0].state === 'Enabled' ? 1 : 0,
            selected[0].distributionId,
            true,
          )
        "
      >
        Deploy
      </button-loading>
      <button-loading
        variant="bg-white"
        class="ml-auto mr-0"
        :spinning="loading"
        @click="onGetDistributions()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Refresh
      </button-loading>
    </div>

    <div
      :class="currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
        ? 'default-table'
        : 'mt-2'
      "
    >
      <div class="table-actions d-flex">
        <b-dropdown
          id="dropdown-2"
          :text="selectedStateText"
          variant="bg-white"
          class="dropdown-icon-wrapper"
        >
          <template #button-content>
            <span>{{ selectedStateText }}</span>
            <GjIcon
              class="icon-right"
              name="ArrowDown"
            />
          </template>
          <template v-for="option in stateOptions">
            <b-dropdown-item
              :key="option.id"
              @click="changeState(option.id)"
            >
              {{ option.text }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
        <b-input-group class="w-auto ml-auto">
          <b-input-group-prepend is-text>
            <b-icon-search @click="onChangefilterTerm" />
          </b-input-group-prepend>
          <b-form-input
            id="filterInput"
            v-model.trim="filterTerm"
            type="search"
            placeholder="Type to Search"
            class="mr-0"
          />
        </b-input-group>
      </div>
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          select-mode="single"
          :items="distributions"
          :fields="fields"
          :class="{ loaded: !loading }"
          :busy="loading"
          show-empty
          selectable
          responsive
          @row-selected="onRowSelected"
        >
          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-3"
            >
              <b-spinner variant="primary" />
            </div>
            <no-data
              v-else
              no-data-text="No distributions have been added yet..."
              create-text="+ Create Distributions"
              access-page="Distributions"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-distribution' })"
            />
          </template>

          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #cell(distributionId)="data">
            <a
              class="clickable"
              @click="idClick(data.value)"
            >{{
              data.value
            }}</a>
          </template>

          <template #cell(domainName)="data">
            <a
              v-clipboard:copy="data.value"
              v-clipboard:success="copySuccess"
            >
              {{ data.value }}
              <GjIcon
                class="ml-auto"
                name="Copy"
                size="17"
              />
            </a>
            <a
              v-if="distributionsSize < 2"
              href="https://docs.captain.gjirafa.tech/cdn/user-guide/working-with-distributions"
              target="_blank"
              class="ml-auto"
            >
              <img
                width="20"
                :src="require('@/assets/svg/question.svg')"
                alt="questionmark"
              >
            </a>
          </template>

          <template #cell(comment)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(origins)="data">
            <template v-if="data.value.length === 1">
              {{ data.value[0] }}
            </template>
            <template
              v-for="(origin, originsIndex) in data.value"
              v-else
            >
              <span :key="originsIndex++">
                {{ origin
                }}<template v-if="originsIndex < data.value.length">,
                </template>
              </span>
            </template>
          </template>

          <template #cell(alternateDomainNames)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(status)="data">
            <span
              class="gj-tag"
              :class="findStatus(data.value)"
            >{{
              data.value
            }}</span>
          </template>

          <template #cell(lastModified)="data">
            {{ formatOffsetDateTime(data.value) }}
          </template>
        </b-table>
        <table-busy v-if="loading && distributions.length > 0" />
      </div>

      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-1">
        <div
          :class="{
            'opacity-7':
              loading || removingDistribution || updatingDistribution,
          }"
        >
          <template v-for="distribution in distributions">
            <card
              :key="distribution.distributionId"
              :fields="fields"
              :item="distribution"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item
                    v-if="$can('get', 'distributions', 'cdn')"
                    @click="idClick(distribution.distributionId)"
                  >
                    Distribution Settings
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('update', 'distributions', 'cdn') &&
                      distribution.state === 'Disabled'
                    "
                    @click="toggleState(1, distribution.distributionId)"
                  >Enable</b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('update', 'distributions', 'cdn') &&
                      distribution.state === 'Enabled'
                    "
                    @click="toggleState(0, distribution.distributionId)"
                  >
                    Disable
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'distributions', 'cdn')"
                    @click="onDeleteDistributionDialog(distribution.distributionId)"
                  >Delete</b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('update', 'distributions', 'cdn')"
                    @click="
                      toggleState(
                        distribution.state === 'Enabled' ? 1 : 0,
                        distribution.distributionId,
                        true,
                      )
                    "
                  >
                    Deploy
                  </b-dropdown-item>
                </b-dropdown>
              </template>
              <template #distributionId>
                <a
                  class="link"
                  @click="idClick(distribution.distributionId)"
                >{{
                  distribution.distributionId
                }}</a>
              </template>
              <template #domainName>
                <a
                  v-clipboard:copy="distribution.domainName"
                  v-clipboard:success="copySuccess"
                >
                  {{ distribution.domainName }}
                  <GjIcon
                    class="ml-auto"
                    name="Copy"
                    size="17"
                  />
                </a>
                <a
                  v-if="distributionsSize < 2"
                  href="https://docs.captain.gjirafa.tech/cdn/user-guide/working-with-distributions"
                  target="_blank"
                  class="ml-auto"
                >
                  <img
                    width="20"
                    :src="require('@/assets/svg/question.svg')"
                    alt="questionmark"
                  >
                </a>
              </template>
              <template #origins>
                <div>
                  <template v-if="distribution.origins.length === 1">
                    {{ distribution.origins[0] }}
                  </template>
                  <template
                    v-for="(origin, originsIndex) in distribution.origins"
                    v-else
                  >
                    <span :key="originsIndex++">
                      {{ origin
                      }}<template v-if="originsIndex < distribution.origins.length">,
                      </template>
                    </span>
                  </template>
                </div>
              </template>
              <template #status>
                <span
                  class="gj-tag"
                  :class="findStatus(distribution.status)"
                >{{
                  distribution.status
                }}</span>
              </template>
              <template #lastModified>
                {{ formatOffsetDateTime(distribution.lastModified) }}
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading || removingDistribution || updatingDistribution"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && distributions.length === 0">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No distributions have been added yet..."
              create-text="+ Create Distributions"
              access-page="Distributions"
              :has-permissions="hasPermissions"
              @action="$router.push({ name: 'create-distribution' })"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="distributions.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useClipboard } from '@vueuse/core'
import Card from '@/components/card/Card.vue'
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'Distributions',
  components: {
    Card,
    BIconSearch,
  },
  data() {
    return {
      selectedState: 2,
      originsIndex: 1,
      cnamesIndex: 1,
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'distributionId',
          label: 'ID',
          variant: 'small',
          isPrimary: true,
        },
        { key: 'domainName', class: 'd-flex' },
        { key: 'comment', isPrimary: true },
        'origins',
        { key: 'alternateDomainNames', label: 'CNAMEs' },
        'status',
        'state',
        { key: 'lastModified', variant: 'time' },
      ],
      selected: [],
      currentPage: 1,
      stateOptions: [
        { id: 0, text: 'Disabled' },
        { id: 1, text: 'Enabled' },
        { id: 2, text: 'Any State' },
      ],
      clickedState: null,
      hasPermissions: true,
      filterTerm: '',
      typingTimer: null,
      doneTypingInterval: 500,
    }
  },
  computed: {
    isSelected() {
      return !!this.selected[0]
    },
    canEnable() {
      return this.isSelected && this.selected[0].state === 'Disabled'
    },
    canDisable() {
      return this.isSelected && this.selected[0].state === 'Enabled'
    },
    distributions() {
      return this.$store.state.cdn.distributions
    },
    resultInfo() {
      return this.$store.state.cdn.distributionsResultInfo
    },
    loading() {
      return this.$store.state.cdn.fetchingDistributions
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    distributionsSize() {
      return this.$store.getters['cdn/distributionsSize']
    },
    selectedStateText() {
      return this.stateOptions.find(opt => opt.id === this.selectedState).text
    },
    removingDistribution() {
      return this.$store.state.cdn.removingDistribution
    },
    updatingDistributionState() {
      return this.$store.state.cdn.updatingDistributionState
    },
    enableDistribution() {
      return this.clickedState === 1 && this.updatingDistributionState
    },
    disableDistribution() {
      return this.clickedState === 0 && this.updatingDistributionState
    },
    updatingDistribution() {
      return this.$store.state.cdn.updatingDistribution
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  watch: {
    currentPage() {
      this.onGetDistributions()
    },
    filterTerm() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        this.onChangefilterTerm()
      }, this.doneTypingInterval)
    },
  },
  created() {
    this.onGetDistributions()
  },
  methods: {
    onRowSelected(items) {
      this.selected = items
    },
    onChangefilterTerm() {
      if (this.currentPage === 1) {
        this.onGetDistributions()
      } else {
        this.currentPage = 1
      }
    },

    idClick(distributionId) {
      this.$router.push({
        name: 'distribution-general',
        params: { id: distributionId },
      })
    },

    updateState(state, distId) {
      this.$store
        .dispatch('cdn/updateDistributionState', {
          distributionId: distId,
          stateId: state,
        })
        .then(() => {
          this.selected = []
          this.$refs.table.clearSelected()
          this.successToast('Distribution was updated')
          this.onGetDistributions()
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },

    toggleState(state, distId) {
      if (state === 0) {
        this.confirmDelete(
          'Disable Distribution',
          'Are you sure you want to disable this distribution?',
          'Disable',
        ).then(ok => {
          if (ok) {
            this.updateState(state, distId)
          }
        })
      } else {
        this.updateState(state, distId)
      }
    },
    onDeleteDistributionDialog(distId) {
      this.confirmDelete(
        'Delete Distribution',
        'Are you sure you want to delete this distribution?',
      ).then(ok => {
        if (ok) {
          this.deleteDistribution(distId)
        }
      })
    },
    onGetDistributions() {
      this.$store
        .dispatch('cdn/fetchDistributions', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          state: this.selectedState,
          term: this.filterTerm,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
            || 'Something went wrong while fetching distributions.',
          )
        })
    },
    deleteDistribution(id) {
      this.$store
        .dispatch('cdn/removeDistribution', id)
        .then(() => {
          this.successToast('Distribution was removed')
          this.selected = []
          this.onGetDistributions()
        })
        .catch(error => {
          this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },
    changeState(id) {
      this.selectedState = id
      if (this.currentPage === 1) {
        this.onGetDistributions()
      } else {
        this.currentPage = 1
      }
    },
    onCopy(iconName) {
      const { copy } = useClipboard()
      copy(iconName)
      this.copySuccess()
    },
    findStatus(s) {
      const status = s.toLowerCase()
      if (status === 'deployed') return 'success'
      if (status === 'deploying') return 'primary'
      if (status === 'failed') return 'danger'
      return ''
    },
  },
}
</script>
<style lang="scss" scoped>
.distribution-main {
  .btn {
    @media (max-width: 767px) {
      margin-bottom: 5px;
    }
  }
}
</style>
